import { Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Subject, switchMap, takeUntil, Observable, of, map, lastValueFrom } from 'rxjs';
import { ExampleData } from '../../models/example-data.model';
import { Option } from '../../../models/option.model';
import { ExampleService } from '../../services/example.service';
import { Component } from '@angular/core';
import { SimpleFormComponent } from '../../../components/abstract/simple/simple-form.component';

@Component({
  selector: 'app-example-form',
  templateUrl: './example-form.component.html',
  styleUrls: ['./example-form.component.scss']
})
export class ExampleFormComponent extends SimpleFormComponent {

  override componentClassName: string = 'ExampleFormComponent';

  // 下拉選單的 Options 資料
  exampleOptions: Option[] = [];

  // 動態下拉選單的 Options 資料
  dynamicOptions: Option[] = [];

  // 自動完成的 Suggestions 資料
  exampleDataSuggestions: ExampleData[] = [];
  // 自動完成欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private exampleDataSubject$ = new Subject<void>();

  // 自動完成 + 下拉的 Suggestions 資料
  allExampleData?: ExampleData[];
  // 自動完成 + 下拉的 Suggestions 資料
  allExampleDataSuggestions: ExampleData[] = [];
  // 自動完成 + 下拉欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private allExampleDataSubject$ = new Subject<void>();

  constructor(private exampleService: ExampleService) {
    super();
  }

  override initFormGroup(): void {
    console.log('ExampleFormComponent initFormGroup');

    this.formGroup = this.formBuilder.nonNullable.group({
      input: '',
      calendar: '',
      dropdown: ['', Validators.required],
      autoCompleteCode: '',
      inputNumber: null,
      calendarRange: '',
      custValidation: ['', this.custValidator()],
      dynamicDropdown: '',
      autoCompleteDropdownCode: ''
    });

    // 設定欄位變更訂閱
    this.formControl('dropdown').valueChanges.pipe(
      takeUntil(this._destroying$)
    ).subscribe(value => {
      this.setCalendarRangeDisable();
    });
  }

  override initFormControlDisabled(): void {
    this.setCalendarRangeDisable();
  }

  /**
   * 下拉選單 = N，日期區間不能輸入。
   * 建議把這種欄位的控制單獨寫成一個 method，因為很多地方可能都會需要觸發，就不用重複寫。
   * 裡面取值都用 getRawValue() 來取。
   * 如果用 this.formGroup.value.xxxx 去取，在欄位的 valueChanges 的時候取自己欄位的值會是前一個狀態的值。
   *
   * 這個應該在欄位變更的時候呼叫，以及 initFormControlDisabled 的時候呼叫。
   */
  setCalendarRangeDisable(): void {
    if (this.formControl('dropdown').getRawValue() == 'N') {
      this.formControl('calendarRange').disable();
    } else {
      this.formControl('calendarRange').enable();
    }
  }

  override initStaticOptions(): void {
    console.log('ExampleFormComponent initStaticOptions');
    // 下拉選單選項
    this.exampleOptions = [
      {
        id: 'option.yes',
        label: this.translateService.instant('option.yes'),
        value: 'Y'
      },
      {
        id: 'option.no',
        label: this.translateService.instant('option.no'),
        value: 'N'
      }
    ];
  }

  override async initFormData(): Promise<any> {
    this.formGroup.patchValue({
      dropdown: 'N'
    });
    await this.setDynamicOptions();
  }

  /**
   * 建議這些要在程式一開始前就取回來的東西分不同 function 處理
   */
  async setDynamicOptions(): Promise<void> {
    console.log('setDynamicOptions start');
    this.dynamicOptions = await lastValueFrom(
      this.exampleService.queryAllExampleDataOptions().pipe(takeUntil(this._destroying$))
    );
    console.log('setDynamicOptions end');
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override afterLangChange(): void {
    this.languageService.processOptionTranslation(this.exampleOptions);
  }

  custValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 欄位要做什麼驗證
      const error = formControl.parent?.get('custValidation')?.value == '1';

      // 如果有錯
      if (error) {
        // 回傳 ValidationErrors，duplicate 是 error 的 key
        return { duplicate: true };
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  searchExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.exampleDataSubject$.observed) {
      this.exampleDataSubject$.pipe(
        switchMap(keyword => this.exampleService.queryExampleDatas(keyword!)),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.exampleDataSuggestions = data;
        console.log('this.exampleDataSuggestions = ' + this.exampleDataSuggestions.length);
      });
    }

    this.exampleDataSubject$.next(event.query);
  }

  searchAllExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.allExampleDataSubject$.observed) {
      this.allExampleDataSubject$.pipe(
        switchMap(keyword => {
          // 如果 allExampleData 已經有取過值了就用它，不然就呼叫 API 取
          let data: Observable<ExampleData[]> = this.allExampleData ? of(this.allExampleData) : this.exampleService.queryAllExampleDatas()
          return data.pipe(
            map(data => {
              console.log('searchAllExampleDataSuggestions = ' + keyword);
              // 把回傳所有的值塞到 allExampleData
              this.allExampleData = data;
              // 根據輸入的關鍵字過濾
              return data.filter(exampleData => exampleData.exampleDataName.indexOf(keyword!) >= 0)
            })
          );
        }),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.allExampleDataSuggestions = data;
      });
    }

    this.allExampleDataSubject$.next(event.query);
  }

  someAction(): void {
    // 該做的驗證檢查還是要自己判斷
    this.submitted = true;
    console.log(JSON.stringify(this.formGroup.getRawValue()));
    if (this.formGroup.valid) {
      this.waitAutoCompleteCompleted(
        () => {
          console.log(JSON.stringify(this.formGroup.getRawValue()));
          // complete 後才要實際去做事情
          this.systemMessageService.info('做某些事情');
          this.submitted = false;
        }
      );
    }
  }
}
